import { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { toast } from 'react-hot-toast'
import { useAuthStore } from '../store/authStore'
import { useRatingStore } from '../store/ratingStore'
import { authAPI, recommendationAPI, ratingAPI } from '../services/api'
import { ImageInfo, RecommendationResponse, SimulatedPreferenceProfile } from '../types'
// 单张图像的评分状态
interface ImageRatingState {
  image: ImageInfo
  qualityScore: number | null
  preferenceScore: number | null
   taskMatchScore: number | null
  isSubmitted: boolean
}

interface RatedImageRecord {
  image: ImageInfo
  qualityScore: number
  preferenceScore: number
   taskMatchScore?: number
  batchNumber: number
  createdAt: string | undefined
}
const hasImagePrompt = (image: ImageInfo): boolean => {
  return Boolean(
    image.prompt?.trim() ||
    image.prompt_cn?.trim()
  )
}

const isImageRatingComplete = (
  imageState: ImageRatingState
): boolean => {
  return (
    imageState.qualityScore !== null &&
    imageState.preferenceScore !== null &&
    (
      !hasImagePrompt(imageState.image) ||
      imageState.taskMatchScore !== null
    )
  )
}

export default function ScoringPage() {
  const navigate = useNavigate()
  const { user, isAuthenticated, setUser } = useAuthStore()
  const { addRating, setCurrentBatch, progress, setProgress } = useRatingStore()

  const [images, setImages] = useState<ImageRatingState[]>([])
  const [isLoading, setIsLoading] = useState(true)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [batchNumber, setBatchNumber] = useState(0)
  const [isColdStart, setIsColdStart] = useState(true)
  const [ratedHistory, setRatedHistory] = useState<RatedImageRecord[]>([])
  const [previewImage, setPreviewImage] = useState<ImageInfo | null>(null)
  const [isReminderOpen, setIsReminderOpen] = useState(true)
  const [persona, setPersona] = useState<SimulatedPreferenceProfile | null>(null)
  const [isPersonaLoading, setIsPersonaLoading] = useState(true)
  // 检查认证状态
  useEffect(() => {
    if (!isAuthenticated || !user) {
      navigate('/register')
    }
  }, [isAuthenticated, user, navigate])

  // 首次评分前抽取一次稳定的模拟用户偏好；同一账号后续批次不重抽。
  useEffect(() => {
    if (!user) return
    const loadPersona = async () => {
      setIsPersonaLoading(true)
      try {
        const profile = await authAPI.getSimulatedPreferences()
        setPersona(profile)
      } catch (error) {
        console.error('加载模拟用户偏好失败:', error)
        toast.error('无法加载模拟用户偏好，请刷新后重试')
      } finally {
        setIsPersonaLoading(false)
      }
    }
    void loadPersona()
  }, [user?.user_id])

  // 加载推荐图像（带本地状态恢复）和进度
  useEffect(() => {
    if (user && persona) {
      const storageKey = `scoring_state_user_${user.user_id}`
      const saved = typeof window !== 'undefined' ? window.localStorage.getItem(storageKey) : null

      if (saved) {
        try {
          const parsed = JSON.parse(saved) as {
            images: ImageRatingState[]
            batchNumber: number
            isColdStart: boolean
          }
          setImages(parsed.images || [])
          setBatchNumber(parsed.batchNumber || 0)
          setIsColdStart(parsed.isColdStart ?? true)
          setIsLoading(false)
        } catch {
          loadRecommendations()
        }
      } else {
        loadRecommendations()
      }

      loadProgress()
      loadRatedHistory()
    }
  }, [user, persona])

  // 加载用户进度
  // 加载用户进度
const loadProgress = async () => {
  if (!user) return null

  try {
    const response = await ratingAPI.getUserProgress(user.user_id)
    setProgress(response)

    if (user.completed !== response.completed) {
      setUser({
        ...user,
        completed: response.completed,
      })
    }

    // 达到条件后只提醒，不自动进入第二阶段。
    if (response.completed && typeof window !== 'undefined') {
      const noticeKey =
        `phase2_eligible_notice_user_${user.user_id}`

      if (!window.sessionStorage.getItem(noticeKey)) {
        toast.success(
          '第一阶段评分数量已经满足要求。你可以继续评分，也可以进入第二阶段评分。',
          { duration: 7000 }
        )
        window.sessionStorage.setItem(noticeKey, '1')
      }
    }

    // 这里的 6 是冷启动图片数量（六个 meta arms 各一张）
    if (response.total_ratings >= 6 && isColdStart) {
      setIsColdStart(false)
    }

    // 必须返回进度对象
    return response
  } catch (error) {
    console.error('获取用户进度失败:', error)
    toast.error('获取进度信息失败')

    // 获取失败时返回 null
    return null
  }
}

  // 加载推荐图像
  const loadRecommendations = async () => {
    if (!user) return

    setIsLoading(true)
    try {
      let response: RecommendationResponse

      if (isColdStart) {
        // 冷启动：6 张（覆盖所有 meta arms）
        response = await recommendationAPI.getColdStart(6)
        setIsColdStart(false)
      } else {
        // 批次推荐：6 张
        response = await recommendationAPI.getNextBatch(6)
      }

      // 初始化评分状态
      const imageStates: ImageRatingState[] = response.images.map(image => ({
        image,
        qualityScore: null,
        preferenceScore: null,
        taskMatchScore: null,
        isSubmitted: false
      }))

      setImages(imageStates)
      setBatchNumber(response.batch_number)
      setCurrentBatch(response.images)

      // Persist current batch to localStorage so refresh keeps state
      if (user && typeof window !== 'undefined') {
        const storageKey = `scoring_state_user_${user.user_id}`
        window.localStorage.setItem(
          storageKey,
          JSON.stringify({
            images: imageStates,
            batchNumber: response.batch_number,
            isColdStart: response.is_cold_start,
          }),
        )
      }

      toast.success(`已加载 ${response.images.length} 张图像`)

    } catch (error: any) {
      console.error('加载推荐图像失败:', error)
      toast.error(error.response?.data?.detail || '加载图像失败，请重试')
    } finally {
      setIsLoading(false)
    }
  }

  const loadRatedHistory = async () => {
    if (!user) return

    try {
      const ratings = await ratingAPI.getUserRatings(user.user_id)
      if (!ratings.length) {
        setRatedHistory([])
        return
      }

      const imageDetails: (RatedImageRecord | null)[] = await Promise.all(
        ratings.map(async (rating) => {
          try {
            const image = await recommendationAPI.getImageInfo(rating.image_id)
            const record: RatedImageRecord = {
              image,
              qualityScore: rating.quality_score,
              preferenceScore: rating.preference_score,
              taskMatchScore: rating.task_match_score,
              batchNumber: rating.batch_number,
              createdAt: rating.created_at,
            }
            return record
          } catch {
            return null
          }
        })
      )

      const validDetails = imageDetails.filter(
        (item): item is RatedImageRecord => item !== null
      )

      setRatedHistory(validDetails.reverse())
    } catch (error) {
      console.error('加载已评分图像失败:', error)
    }
  }

  const appendRatedHistory = (records: RatedImageRecord[]) => {
    setRatedHistory(prev => {
      const existingIds = new Set(prev.map(record => `${record.image.image_id}-${record.batchNumber}`))
      const nextRecords = records.filter(record => !existingIds.has(`${record.image.image_id}-${record.batchNumber}`))
      return [...nextRecords.reverse(), ...prev]
    })
  }

  // 更新图像评分
  // 更新图像评分
    const updateImageScore = (
      index: number,
      type: 'quality' | 'task' | 'preference',
      score: number
    ) => {
      setImages(prev => {
        const newImages = [...prev]

        if (type === 'quality') {
          newImages[index].qualityScore = score
        } else if (type === 'task') {
          newImages[index].taskMatchScore = score
        } else {
          newImages[index].preferenceScore = score
        }

        // 同步更新后的评分到本地存储，刷新页面后仍可恢复
        if (user && typeof window !== 'undefined') {
          const storageKey = `scoring_state_user_${user.user_id}`

          window.localStorage.setItem(
            storageKey,
            JSON.stringify({
              images: newImages,
              batchNumber,
              isColdStart,
            })
          )
        }

        return newImages
      })
    }
      // 批量提交所有已完成评分的图像
    // 一次性提交当前批次，并自动获取下一批
    // 一次性提交当前批次，并自动获取下一批
  const submitAllRatings = async () => {
      if (!user) return

      const ratedImages = images.filter(
        img => !img.isSubmitted && isImageRatingComplete(img)
      )

      if (ratedImages.length === 0) {
        toast.error('请至少完成一张图像显示的所有必填评分项')
        return
      }

      setIsSubmitting(true)

      try {
        const unsubmittedImages = ratedImages

        // 当前批次尚未提交时，先批量提交
        if (unsubmittedImages.length > 0) {
          const ratings = unsubmittedImages.map(imageState => ({
            image_id: imageState.image.image_id,
            quality_score: imageState.qualityScore!,
            task_match_score: hasImagePrompt(imageState.image)
              ? imageState.taskMatchScore!
              : undefined,
            preference_score: imageState.preferenceScore!,
            batch_number: batchNumber,
          }))

          await ratingAPI.createRatingBatch({ ratings })

          // 标记当前批次已经提交
          const submittedImages = images.map(img => ({
            ...img,
            isSubmitted: true,
          }))

          setImages(submittedImages)

          if (typeof window !== 'undefined') {
            const storageKey = `scoring_state_user_${user.user_id}`

            window.localStorage.setItem(
              storageKey,
              JSON.stringify({
                images: submittedImages,
                batchNumber,
                isColdStart,
              })
            )
          }

          ratings.forEach(rating => {
            addRating({
              user_id: user.user_id,
              ...rating,
            })
          })

          appendRatedHistory(
            unsubmittedImages.map(imageState => ({
              image: imageState.image,
              qualityScore: imageState.qualityScore!,
              taskMatchScore: hasImagePrompt(imageState.image)
                ? imageState.taskMatchScore!
                : undefined,
              preferenceScore: imageState.preferenceScore!,
              batchNumber,
              createdAt: undefined,
            }))
          )

          await loadProgress()

          toast.success(`成功提交 ${ratings.length} 条评分`)
        }

        // 提交成功后自动获取下一批；若之前加载失败，也可再次点击重试
        await loadRecommendations()
      } catch (error: any) {
        console.error('批量提交评分失败:', error)
        toast.error(error.response?.data?.detail || '提交评分失败，请重试')
      } finally {
        setIsSubmitting(false)
      }
    }
  // 计算已完成评分的图像数量
 // 计算当前批次已经完成三个维度评分的数量
  const getCompletedCount = () => {
    return images.filter(
      img =>
        isImageRatingComplete(img)
    ).length
}

  // 计算总评分进度
  const getTotalProgress = () => {
    if (!progress) return { current: 0, required: 40 }
    return {
      current: progress.total_ratings,
      required: progress.required_total
    }
  }

  // 计算高风格偏好评分进度
  const getHighPreferenceProgress = () => {
    if (!progress) return { current: 0, required: 8 }
    return {
      current: progress.high_preference_ratings,
      required: progress.required_high_preference
    }
  }

  // 检查是否可以提交所有评分
  // 当前批次全部完成后才允许提交
  // 检查当前批次是否至少有一张已完成且未提交的图像可提交
  const canSubmitAll = () => {
    return images.some(
      img => !img.isSubmitted && isImageRatingComplete(img)
    )
  }

  const canEnterPhaseTwo = Boolean(progress?.completed)

  const handleEnterPhaseTwo = () => {
    const totalCurrent = progress?.total_ratings ?? 0
    const totalRequired = progress?.required_total ?? 40
    const highCurrent = progress?.high_preference_ratings ?? 0
    const highRequired = progress?.required_high_preference ?? 8

    if (!canEnterPhaseTwo) {
      const missing: string[] = []

      if (totalCurrent < totalRequired) {
        missing.push(`还需 ${totalRequired - totalCurrent} 张评分`)
      }

      if (highCurrent < highRequired) {
        missing.push(`还需 ${highRequired - highCurrent} 张个人风格偏好分 ≥ 4 的图片`)
      }

      toast.error(`尚未满足进入第二阶段的条件：${missing.join('，')}。`)
      return
    }

    navigate('/phase2')
  }

  const likedImages = ratedHistory.filter(record => record.preferenceScore >= 4)
  const neutralImages = ratedHistory.filter(record => record.preferenceScore < 4)

  if (!user) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[60vh]">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500 mb-4"></div>
        <p className="text-lg text-gray-600">正在恢复用户信息...</p>
      </div>
    )
  }

  if (isLoading || isPersonaLoading || !persona) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[60vh]">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500 mb-4"></div>
        <p className="text-lg text-gray-600">正在加载图像...</p>
      </div>
    )
  }

  return (
    <div className="max-w-7xl mx-auto lg:grid lg:grid-cols-[minmax(0,1fr)_320px] lg:gap-8">
      <div className="min-w-0">
      {/* 页面标题和进度 */}
      <div className="mb-8">
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-6">
          <div>
            <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 leading-snug">
              图像评分任务
            </h1>
            <p className="mt-2 text-sm sm:text-base text-gray-600 leading-relaxed">
              请分别从图像质量、任务匹配和模拟用户风格偏好三个维度独立评分；右侧加粗标签是本轮模拟用户的偏好设定。
            </p>
          </div>

          <div className="flex items-center space-x-4">
            <div className="text-right">
              <div className="text-sm text-gray-500">当前批次</div>
              <div className="text-2xl font-bold text-blue-600">#{batchNumber}</div>
            </div>
            <div className="h-10 w-px bg-gray-300"></div>
            <div className="text-right">
              <div className="text-sm text-gray-500">已完成</div>
              <div className="text-2xl font-bold text-green-600">{getCompletedCount()}/{images.length}</div>
            </div>
          </div>
        </div>

        {isReminderOpen ? (
          <div className="fixed inset-0 z-50 overflow-y-auto bg-black/60 p-4 sm:p-8">
            <div className="flex min-h-full items-center justify-center">
              <div className="w-full max-w-3xl rounded-2xl border border-amber-200 bg-amber-50 p-5 text-sm text-amber-900 shadow-2xl sm:p-8">
                <h2 className="mb-3 text-xl font-bold sm:text-2xl">你将模拟的用户风格偏好</h2>

                <div className="mb-5 rounded-xl border border-violet-200 bg-white/80 p-4">
                  <p className="font-semibold text-violet-950">请以这位模拟用户的风格偏好完成评分，而不是按你本人的审美打分。</p>
                  <div className="mt-3 flex flex-wrap gap-2">
                    {persona.common_tags.map(tag => <span key={tag} className="rounded-full bg-violet-100 px-3 py-1 text-sm font-bold text-violet-800">{tag}</span>)}
                    {persona.extended_tags.map(tag => <span key={tag} className="rounded-full border border-violet-300 bg-white px-3 py-1 text-sm font-semibold text-violet-700">{tag}</span>)}
                  </div>
                  <p className="mt-3 text-xs leading-5 text-violet-800">这些是本账号首次开始标注时随机抽取并固定的模拟设定；它们不等于真人偏好标签。</p>
                </div>

          <ul className="space-y-3 leading-6">
            <li>
              • <strong>模拟用户的风格偏好（最重要）：</strong>
              判断这位模拟用户是否会喜欢这张图片，重点关注风格、配色、氛围和整体审美，
              而不是考虑图像的具体内容。
              <ul className="mt-3 ml-5 list-disc space-y-2">
                <li>
                  例如：题目要求生成“一只猫”，图片准确画出了一只猫，画面清晰、细节完整，
                  并采用水墨画风格。因此图像质量和任务匹配都可以较高；
                  但如果该模拟用户的标签偏向赛博朋克、不偏好水墨画，模拟用户偏好分仍然应该较低。
                </li>
                <li>
                  反之，即使图片存在少量细节问题，只要它的风格、配色或氛围非常符合模拟用户设定，
                  个人风格偏好分也可以较高。
                </li>
              </ul>
            </li>

            <li>
              • <strong>图像质量：</strong>
              判断画面是否清晰自然、细节是否完整，以及是否存在肢体畸形、
              物体变形或文字乱码等问题。
            </li>

            <li>
              • <strong>任务匹配：</strong>
              判断图片是否准确呈现题目要求的主体、动作、场景、风格和关键元素。没有题目或提示词的图片不需要填写此项。
            </li>


            <li>
              • 至少完成 <strong>40 张图片</strong>评分，其中个人风格偏好分
              <strong> ≥ 4 分</strong>的图片至少有 <strong>8 张</strong>。
              请按照真实感受评分，不要为了达到数量要求而刻意提高分数。
            </li>
                </ul>

                <div className="mt-6 flex justify-center border-t border-amber-200 pt-5">
                  <button
                    type="button"
                    onClick={() => setIsReminderOpen(false)}
                    className="rounded-lg bg-amber-700 px-8 py-3 font-semibold text-white shadow hover:bg-amber-800 focus:outline-none focus:ring-2 focus:ring-amber-500 focus:ring-offset-2"
                  >
                    阅读完毕
                  </button>
                </div>
              </div>
            </div>
          </div>
        ) : (
          <div className="sticky top-4 z-30 mb-6 flex justify-center">
            <button
              type="button"
              onClick={() => setIsReminderOpen(true)}
              aria-label="再次查看评分提醒"
              className="inline-flex items-center gap-3 rounded-xl border-2 border-amber-300 bg-amber-50 px-5 py-3 text-sm font-semibold text-amber-900 shadow-lg transition hover:border-amber-400 hover:bg-amber-100 focus:outline-none focus:ring-2 focus:ring-amber-500 focus:ring-offset-2"
            >
              <span aria-hidden="true" className="text-lg">ⓘ</span>
              <span>评分提醒</span>
              <span className="rounded-full bg-amber-200 px-2.5 py-1 text-xs font-medium text-amber-900">
                点击再次查看
              </span>
            </button>
          </div>
        )}

        {canEnterPhaseTwo && (
          <div className="mb-6 rounded-2xl border border-green-200 bg-green-50 p-4 text-sm text-green-900">
            <strong>你已经满足进入第二阶段的条件。</strong>
            你可以继续评分更多图片，也可以点击下方“进入第二阶段评分”按钮。
          </div>
        )}

        {/* 进度卡片 */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
          <div className="bg-white rounded-xl shadow-md p-6 border border-gray-200">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-gray-900">总评分进度</h3>
              <span className="px-3 py-1 bg-blue-100 text-blue-800 text-sm font-medium rounded-full">
                {getTotalProgress().current}/{getTotalProgress().required}
              </span>
            </div>
            <div className="relative pt-1">
              <div className="flex mb-2 items-center justify-between">
                <div>
                  <span className="text-xs font-semibold inline-block text-blue-600">
                    {Math.round((getTotalProgress().current / getTotalProgress().required) * 100)}%
                  </span>
                </div>
              </div>
              <div className="overflow-hidden h-2 mb-4 text-xs flex rounded bg-blue-100">
                <div
                  style={{ width: `${Math.min(100, (getTotalProgress().current / getTotalProgress().required) * 100)}%` }}
                  className="shadow-none flex flex-col text-center whitespace-nowrap text-white justify-center bg-blue-500 transition-all duration-300"
                ></div>
              </div>
              <p className="text-sm text-gray-600">
                达到 {getTotalProgress().required} 条评分后，可进入第二阶段
              </p>
            </div>
          </div>

          <div className="bg-white rounded-xl shadow-md p-6 border border-gray-200">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-gray-900">高风格偏好评分进度</h3>
              <span className="px-3 py-1 bg-green-100 text-green-800 text-sm font-medium rounded-full">
                {getHighPreferenceProgress().current}/{getHighPreferenceProgress().required}
              </span>
            </div>
            <div className="relative pt-1">
              <div className="flex mb-2 items-center justify-between">
                <div>
                  <span className="text-xs font-semibold inline-block text-green-600">
                    {Math.round((getHighPreferenceProgress().current / getHighPreferenceProgress().required) * 100)}%
                  </span>
                </div>
              </div>
              <div className="overflow-hidden h-2 mb-4 text-xs flex rounded bg-green-100">
                <div
                  style={{ width: `${Math.min(100, (getHighPreferenceProgress().current / getHighPreferenceProgress().required) * 100)}%` }}
                  className="shadow-none flex flex-col text-center whitespace-nowrap text-white justify-center bg-green-500 transition-all duration-300"
                ></div>
              </div>
              <p className="text-sm text-gray-600">
                需要至少 {getHighPreferenceProgress().required} 条偏好分≥4的评分
              </p>
            </div>
          </div>
        </div>
      </div>
      {/* 图像网格 */}
      <div className="mb-10">
        <div className="grid grid-cols-1 min-[360px]:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6">
          {images.map((imageState, index) => (
            <div
              key={imageState.image.image_id}
              className={`bg-white rounded-xl shadow-lg overflow-hidden border-2 transition-all duration-300 ${
                imageState.isSubmitted
                  ? 'border-green-500'
                  : isImageRatingComplete(imageState)
                  ? 'border-blue-500'
                  : 'border-gray-200'
              }`}
            >
              {/* 图像区域 */}
              <div className="relative">
                <img
                  src={imageState.image.image_url}
                  alt={imageState.image.prompt_cn || imageState.image.prompt}
                  className="w-full max-h-64 sm:max-h-80 lg:max-h-96 object-contain bg-black cursor-zoom-in"
                  onClick={() => setPreviewImage(imageState.image)}
                  loading="lazy"
                  decoding="async"
                  onError={(e) => {
                    const target = e.target as HTMLImageElement
                    target.src = `https://via.placeholder.com/400x300?text=Image+${imageState.image.image_id}`
                  }}
                />
              </div>

              {/* 图像信息 */}
              {hasImagePrompt(imageState.image) ? (
                <div className="p-3 sm:p-4 border-b border-gray-100">
                  <p
                    className="text-sm text-gray-600 whitespace-normal break-words leading-6"
                    title={imageState.image.prompt_cn || imageState.image.prompt}
                  >
                    {imageState.image.prompt_cn || imageState.image.prompt}
                  </p>
                </div>
              ) : (
                <div className="p-3 sm:p-4 border-b border-gray-100 text-sm text-gray-500">
                  该图像没有题目或提示词，无需进行任务匹配评分。
                </div>
              )}

              {/* 评分区域 */}
              <div className="p-3 sm:p-4">
                {/* 图像质量评分 */}
                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    图像质量评分 <span className="text-red-500">*</span>
                    <span className="ml-2 text-xs text-gray-500">(技术层面评估)</span>
                  </label>
                  <div className="flex flex-wrap gap-1">
                    {[1, 2, 3, 4, 5].map(score => (
                      <button
                        key={`quality-${score}`}
                        type="button"
                        onClick={() => updateImageScore(index, 'quality', score)}
                        disabled={imageState.isSubmitted}
                        className={`flex-1 min-w-[2.5rem] py-2 text-sm font-medium rounded-lg transition-colors ${
                          imageState.qualityScore === score
                            ? 'bg-blue-500 text-white'
                            : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                        } ${imageState.isSubmitted ? 'opacity-50 cursor-not-allowed' : ''}`}
                      >
                        {score}
                      </button>
                    ))}
                  </div>
                  <div className="flex justify-between text-xs text-gray-500 mt-1">
                    <span>很差</span>
                    <span>一般</span>
                    <span>很好</span>
                  </div>
                </div>

                {/* 只有存在 prompt 时才显示任务匹配评分 */}
                {hasImagePrompt(imageState.image) && (
                  <div className="mb-4">
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      任务匹配评分 <span className="text-red-500">*</span>
                      <span className="ml-2 text-xs text-gray-500">(图像与任务/提示描述的匹配程度)</span>
                    </label>
                    <div className="flex flex-wrap gap-1">
                      {[1, 2, 3, 4, 5].map(score => (
                        <button
                          key={`task-${score}`}
                          type="button"
                          onClick={() => updateImageScore(index, 'task', score)}
                          disabled={imageState.isSubmitted}
                          className={`flex-1 min-w-[2.5rem] py-2 text-sm font-medium rounded-lg transition-colors ${
                            imageState.taskMatchScore === score
                              ? 'bg-blue-500 text-white'
                              : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                          } ${imageState.isSubmitted ? 'opacity-50 cursor-not-allowed' : ''}`}
                        >
                          {score}
                        </button>
                      ))}
                    </div>
                    <div className="flex justify-between text-xs text-gray-500 mt-1">
                      <span>不匹配</span>
                      <span>一般</span>
                      <span>非常匹配</span>
                    </div>
                  </div>
                )}

                {/* 模拟用户风格偏好评分 */}
                <div className="mb-6">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    模拟用户风格偏好评分 <span className="text-red-500">*</span>
                    <span className="ml-2 text-xs text-gray-500">(依据右侧模拟偏好设定)</span>
                  </label>
                  <div className="flex flex-wrap gap-1">
                    {[1, 2, 3, 4, 5].map(score => (
                      <button
                        key={`preference-${score}`}
                        type="button"
                        onClick={() => updateImageScore(index, 'preference', score)}
                        disabled={imageState.isSubmitted}
                        className={`flex-1 min-w-[2.5rem] py-2 text-sm font-medium rounded-lg transition-colors ${
                          imageState.preferenceScore === score
                              ? 'bg-blue-500 text-white'
                              : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                        } ${imageState.isSubmitted ? 'opacity-50 cursor-not-allowed' : ''}`}
                      >
                        {score}
                      </button>
                    ))}
                  </div>
                  <div className="flex justify-between text-xs text-gray-500 mt-1">
                    <span>不喜欢</span>
                    <span>一般</span>
                    <span>很喜欢</span>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* 当图像数量少于3时显示提示 */}
        {images.length < 3 && (
          <div className="text-center py-8">
            <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-blue-100 mb-4">
              <svg className="w-8 h-8 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
              </svg>
            </div>
            <h3 className="text-lg font-medium text-gray-900 mb-2">正在加载更多图像...</h3>
            <p className="text-gray-600">系统正在为您准备个性化的图像推荐。</p>
          </div>
        )}
      </div>

     {/* 控制按钮 */}
<div className="mt-6 md:mt-8 md:sticky md:bottom-6 bg-white rounded-xl shadow-xl border border-gray-200 p-4 sm:p-6">
  <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
    <div className="text-sm text-gray-600">
      <p>
        提示：完成当前批次至少一张图像显示的必填评分项后，即可提交并进入下一批。
      </p>
      <p className="mt-1">
        本任务至少完成 40 张图片评分，其中个人风格偏好分 ≥ 4 分的图片至少 8 张。请按照真实感受评分，不要为了达到数量要求而刻意提高分数。
      </p>
    </div>

    <div className="flex flex-col sm:flex-row gap-4">
      <button
        type="button"
        onClick={submitAllRatings}
        disabled={!canSubmitAll() || isSubmitting}
        className={`px-6 py-3 text-sm font-medium rounded-lg transition-colors ${
          canSubmitAll() && !isSubmitting
            ? 'bg-blue-600 text-white hover:bg-blue-700'
            : 'bg-gray-100 text-gray-400 cursor-not-allowed'
        }`}
      >
        {isSubmitting ? '处理中...' : '提交本批评分并获取下一批'}
      </button>

      <button
        type="button"
        onClick={handleEnterPhaseTwo}
        aria-disabled={!canEnterPhaseTwo}
        className={`px-6 py-3 text-sm font-medium rounded-lg transition-colors ${
          canEnterPhaseTwo
            ? 'bg-green-600 text-white hover:bg-green-700'
            : 'bg-gray-200 text-gray-500 hover:bg-gray-300 cursor-pointer'
        }`}
      >
        进入第二阶段评分
      </button>
    </div>
  </div>

  {/* 进度提示 */}
  <div className="mt-4 pt-4 border-t border-gray-200">
    <div className="text-sm text-gray-600">
      <div className="flex flex-wrap items-center gap-y-2">
        <div className="w-3 h-3 rounded-full bg-green-500 mr-2"></div>
        <span className="mr-4">
          已完成评分: {getCompletedCount()} 张
        </span>

        <div className="w-3 h-3 rounded-full bg-blue-500 mr-2"></div>
        <span className="mr-4">
          待提交: {
            images.filter(
              img =>
                isImageRatingComplete(img) &&
                !img.isSubmitted
            ).length
          } 张
        </span>

        <div className="w-3 h-3 rounded-full bg-gray-300 mr-2"></div>
        <span>
          未评分: {
            images.filter(
              img =>
                !isImageRatingComplete(img)
            ).length
          } 张
        </span>
      </div>
    </div>
  </div>
</div>

      </div>

      <aside className="mt-8 lg:mt-0">
        <div className="lg:sticky lg:top-6 rounded-2xl border border-gray-200 bg-white shadow-lg overflow-hidden">
          <div className="px-5 py-4 border-b border-gray-100 bg-gray-50">
            <h3 className="text-lg font-semibold text-gray-900">正在模拟的用户偏好</h3>
            <p className="mt-1 text-sm text-gray-600">
              请始终以这些加粗标签代表的模拟用户审美来判断图片；下方是该模拟用户已经完成的评分回顾。
            </p>
          </div>

          <div className="max-h-[75vh] overflow-y-auto p-4 space-y-5">
            {/* 模拟用户风格设定 */}
            <section className="pb-4 border-b border-gray-100">
              <h4 className="text-sm font-semibold text-violet-900 mb-3">此用户喜欢的风格</h4>
              <div className="flex flex-wrap gap-2">
                {persona.common_tags.map(tag => <span key={`sidebar-common-${tag}`} className="rounded-lg bg-violet-600 px-3 py-1.5 text-sm font-bold text-white shadow-sm">{tag}</span>)}
                {persona.extended_tags.map(tag => <span key={`sidebar-extended-${tag}`} className="rounded-lg border-2 border-violet-300 bg-violet-50 px-3 py-1.5 text-sm font-bold text-violet-800">{tag}</span>)}
              </div>
              <p className="mt-3 text-xs leading-5 text-gray-500">常见风格标签至少有一个；拓展标签可能为空。不要把标签机械地套用到所有内容，仍需综合风格、配色、光影和画面质量判断。</p>
            </section>

            <section>
              <div className="flex items-center justify-between mb-3">
                <h4 className="text-sm font-semibold text-green-700">偏好 ≥ 4，感兴趣</h4>
                <span className="text-xs text-green-700 bg-green-50 px-2 py-1 rounded-full">{likedImages.length} 张</span>
              </div>
              <div className="space-y-3">
                {likedImages.length > 0 ? likedImages.map(record => (
                  <div key={`${record.image.image_id}-${record.batchNumber}-liked`} className="rounded-xl border border-green-100 bg-green-50/60 p-3">
                    <div className="flex gap-3">
                      <img
                        src={record.image.image_url}
                        alt={record.image.prompt_cn || record.image.prompt}
                        className="h-20 w-20 rounded-lg object-cover flex-shrink-0"
                        loading="lazy"
                        decoding="async"
                      />
                      <div className="min-w-0 flex-1">
                        <p
                          className="text-sm font-medium text-gray-900 line-clamp-2"
                          title={record.image.prompt_cn || record.image.prompt || `图像 ${record.image.image_id}`}
                        >
                          {record.image.prompt_cn || record.image.prompt || `图像 ${record.image.image_id}`}
                        </p>
                        <p className="mt-2 text-xs text-gray-600">质量 {record.qualityScore} / 偏好 {record.preferenceScore}</p>
                        <p className="mt-1 text-xs text-gray-500">批次 #{record.batchNumber}</p>
                      </div>
                    </div>
                  </div>
                )) : (
                  <div className="rounded-xl border border-dashed border-green-200 p-4 text-sm text-gray-500 bg-white">
                    你给偏好分 ≥ 4 的图像会显示在这里。
                  </div>
                )}
              </div>
            </section>

            <section>
              <div className="flex items-center justify-between mb-3">
                <h4 className="text-sm font-semibold text-gray-700">偏好 &lt; 4，一般或不感兴趣</h4>
                <span className="text-xs text-gray-700 bg-gray-100 px-2 py-1 rounded-full">{neutralImages.length} 张</span>
              </div>
              <div className="space-y-3">
                {neutralImages.length > 0 ? neutralImages.map(record => (
                  <div key={`${record.image.image_id}-${record.batchNumber}-neutral`} className="rounded-xl border border-gray-200 bg-gray-50 p-3">
                    <div className="flex gap-3">
                      <img
                        src={record.image.image_url}
                        alt={record.image.prompt_cn || record.image.prompt}
                        className="h-20 w-20 rounded-lg object-cover flex-shrink-0"
                        loading="lazy"
                        decoding="async"
                      />
                      <div className="min-w-0 flex-1">
                        <p
                          className="text-sm font-medium text-gray-900 line-clamp-2"
                          title={record.image.prompt_cn || record.image.prompt || `图像 ${record.image.image_id}`}
                        >
                          {record.image.prompt_cn || record.image.prompt || `图像 ${record.image.image_id}`}
                        </p>
                        <p className="mt-2 text-xs text-gray-600">质量 {record.qualityScore} / 偏好 {record.preferenceScore}</p>
                        <p className="mt-1 text-xs text-gray-500">批次 #{record.batchNumber}</p>
                      </div>
                    </div>
                  </div>
                )) : (
                  <div className="rounded-xl border border-dashed border-gray-200 p-4 text-sm text-gray-500 bg-white">
                    偏好较低的图像会显示在这里，帮助你回顾自己的选择。
                  </div>
                )}
              </div>
            </section>
          </div>
        </div>
      </aside>
      {previewImage && (
  <div
    className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 p-4"
    onClick={() => setPreviewImage(null)}
  >
    <button
      type="button"
      onClick={() => setPreviewImage(null)}
      className="absolute right-5 top-5 text-4xl text-white hover:text-gray-300"
      aria-label="关闭图片预览"
    >
      ×
    </button>

    <img
      src={previewImage.image_url}
      alt={previewImage.prompt_cn || previewImage.prompt}
      className="max-h-[90vh] max-w-[95vw] object-contain"
      onClick={(event) => event.stopPropagation()}
    />
  </div>
)}
    </div>
  )
}
